import { Button } from '@artosera/ui';
import { Box, Lock } from 'lucide-react';
import { useId, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { definitionModule, type ModuleOptionnel } from '@artosera/shared';
import { useBientotMasque } from '../../preferences/bientotStorage.js';
import { ApercuFantome } from './ApercuFantome.js';
import { APERCU_PALIER, ICONES_MODULES } from './catalogue.js';
import { modulesAVenirDuPalier, type ModulesAVenir, type PalierAVenir } from './modulesAVenir.js';
import styles from './BientotSection.module.css';

/** Au-delà de trois modules, les autres se replient derrière « + N autres ». */
const MODULES_VISIBLES = 3;

export interface BientotSectionProps {
  readonly galerieSlug: string;
  readonly compteId: string;
  readonly avenir: ModulesAVenir;
  /** « Voir le palier sur la carte » : filtre la carte puis y mène. */
  readonly onVoirSurLaCarte: (palier: PalierAVenir) => void;
}

function CartePalier({
  palier,
  modules,
  galerieSlug,
  onVoirSurLaCarte,
}: {
  palier: PalierAVenir;
  modules: readonly ModuleOptionnel[];
  galerieSlug: string;
  onVoirSurLaCarte: (palier: PalierAVenir) => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [deplie, setDeplie] = useState(false);
  const idReste = useId();

  const phare = APERCU_PALIER[palier];
  const montrePhare = modules.includes(phare.module);
  const autres = modules.filter((module) => !montrePhare || module !== phare.module);
  const visibles = autres.slice(0, MODULES_VISIBLES);
  const reste = autres.slice(MODULES_VISIBLES);

  const ligne = (module: ModuleOptionnel): JSX.Element => {
    const Icone = ICONES_MODULES[module] ?? Box;
    return (
      <li key={module}>
        <Link className={styles.module} to={`/${galerieSlug}/modules/${module}`}>
          <Icone size={14} strokeWidth={1.5} aria-hidden />
          <span>{t(definitionModule(module).libelle)}</span>
        </Link>
      </li>
    );
  };

  return (
    <section
      className={styles.carte}
      aria-label={t(`regie:modulesAVenir.arrive.${palier}`)}
      data-palier={palier}
    >
      <div className={styles.entete}>
        <span className={styles.palier}>{t(`regie:modulesAVenir.palier.${palier}`)}</span>
        <span className={styles.verrou}>
          <Lock size={14} strokeWidth={1.5} aria-hidden />
          {t(`regie:modulesAVenir.arrive.${palier}`)}
        </span>
        <span className={styles.compte}>
          {t('regie:modulesAVenir.bientot.modules', { count: modules.length })}
        </span>
      </div>

      {montrePhare ? (
        <ApercuFantome module={phare.module} forme={phare.forme} galerieSlug={galerieSlug} />
      ) : null}

      <ul className={styles.liste}>{visibles.map(ligne)}</ul>
      {reste.length > 0 ? (
        <>
          <ul className={styles.liste} id={idReste} hidden={!deplie}>
            {reste.map(ligne)}
          </ul>
          <Button
            variant="ghost"
            size="sm"
            className={styles.plus}
            aria-expanded={deplie}
            aria-controls={idReste}
            onClick={() => setDeplie((valeur) => !valeur)}
          >
            {deplie
              ? t('regie:modulesAVenir.bientot.replier')
              : t('regie:modulesAVenir.bientot.autres', { count: reste.length })}
          </Button>
        </>
      ) : null}

      <Button
        variant="secondary"
        size="sm"
        className={styles.aller}
        onClick={() => onVoirSurLaCarte(palier)}
      >
        {t('regie:modulesAVenir.bientot.voirSurCarte')}
      </Button>
    </section>
  );
}

/**
 * « Bientôt dans votre régie » : une carte par palier verrouillé, son module
 * phare en aperçu fantôme, les autres modules dessous. Entièrement statique :
 * rien ici n'appelle une route d'un module à venir. La section se replie
 * (préférence locale, par compte). Sans palier verrouillé, elle n'est pas montrée.
 */
export function BientotSection({
  galerieSlug,
  compteId,
  avenir,
  onVoirSurLaCarte,
}: BientotSectionProps): JSX.Element | null {
  const { t } = useTranslation('regie');
  const [masque, setMasque] = useBientotMasque(compteId);
  const idCorps = useId();
  if (avenir.aucun) return null;

  return (
    <section className={styles.section} aria-labelledby="bientot-titre" data-ui="dashboard-soon">
      <div className={styles.tete}>
        <div>
          <h2 id="bientot-titre">{t('modulesAVenir.bientot.titre')}</h2>
          <p className={styles.intro}>{t('modulesAVenir.bientot.intro')}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={styles.masquer}
          aria-expanded={!masque}
          aria-controls={idCorps}
          onClick={() => setMasque(!masque)}
        >
          {masque ? t('modulesAVenir.bientot.afficher') : t('modulesAVenir.bientot.masquer')}
        </Button>
      </div>

      <div className={styles.paliers} id={idCorps} hidden={masque}>
        {avenir.paliers.map((palier) => (
          <CartePalier
            key={palier}
            palier={palier}
            modules={modulesAVenirDuPalier(avenir, palier)}
            galerieSlug={galerieSlug}
            onVoirSurLaCarte={onVoirSurLaCarte}
          />
        ))}
      </div>
    </section>
  );
}
